import { useState, useEffect, useMemo, useRef } from 'react';
import {
  Modal,
  HeaderGlobalAction,
  TextInput,
  TextArea,
  Button,
  Select,
  SelectItem,
  Checkbox,
  DatePicker,
  DatePickerInput,
  TimePicker,
  TimePickerSelect,
  SelectItem as TimeSelectItem,
} from '@carbon/react';
import { Calendar as CalendarIcon } from '@carbon/icons-react';
import { useFetcher } from 'react-router';
import { useNotify } from './notify.tsx';
import { format } from 'date-fns';
import {
  BahandiCalendar,
  type BahandiCalendarEvent,
  getCategoryColor,
} from './bahandi-calendar.tsx';

interface CalendarEventData {
  id: string;
  title: string;
  start: string; // ISO string from server
  end: string; // ISO string from server
  type: 'leave' | 'holiday' | 'birthday' | 'custom';
  category?:
    | 'CTRLSPIRE_MEETING'
    | 'SWELLFOX_MEETING'
    | 'CLIENT_MEETING'
    | 'FUN_HOUR'
    | 'TEAM_BUILDING'
    | 'ANNOUNCEMENT';
  status?: string;
  allDay: boolean;
  description?: string | null;
  createdBy?: string;
  createdById?: string; // User ID of the creator
}

interface CalendarData {
  events: CalendarEventData[];
  userTimezone: string;
  currentUserId: string;
  isAdmin: boolean;
  isHRManager: boolean;
}

export function CalendarModalButton() {
  const [isOpen, setIsOpen] = useState(false);
  const [isCreateEventModalOpen, setIsCreateEventModalOpen] = useState(false);
  const [isEditEventModalOpen, setIsEditEventModalOpen] = useState(false);
  const [isDeleteConfirmModalOpen, setIsDeleteConfirmModalOpen] =
    useState(false);
  const fetcher = useFetcher<CalendarData>();
  const createEventFetcher = useFetcher();
  const updateEventFetcher = useFetcher();
  const deleteEventFetcher = useFetcher();
  const notify = useNotify();
  const lastProcessedRef = useRef<string | null>(null);
  const lastUpdateProcessedRef = useRef<string | null>(null);
  const lastDeleteProcessedRef = useRef<string | null>(null);
  const [showCustomEvents, setShowCustomEvents] = useState(true);
  const [showLeaves, setShowLeaves] = useState(true);
  const [showHolidays, setShowHolidays] = useState(true);
  const [showBirthdays, setShowBirthdays] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());

  // Form state for creating/editing events
  const [editingEventId, setEditingEventId] = useState<string | null>(null);
  const [canEditCurrentEvent, setCanEditCurrentEvent] = useState(false);
  const [eventCreator, setEventCreator] = useState<string>('');
  const [selectedStartDate, setSelectedStartDate] = useState<Date | null>(null);
  const [selectedEndDate, setSelectedEndDate] = useState<Date | null>(null);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('09:30');
  const [isAllDay, setIsAllDay] = useState(false);
  const [eventTitle, setEventTitle] = useState('');
  const [eventCategory, setEventCategory] = useState('');
  const [eventDescription, setEventDescription] = useState('');

  // Fetch data when modal opens
  const handleOpen = () => {
    setIsOpen(true);
    // Fetch calendar data
    fetcher.load('/api/calendar-events');
  };

  const userTimezone = fetcher.data?.userTimezone || 'Asia/Manila';

  // Handle day cell click to create event
  const handleSelectSlot = (slotInfo: { start: Date; end: Date }) => {
    // When clicking a day cell, set both start and end to the SAME day
    // (react-big-calendar gives us start = clicked day, end = next day)
    setSelectedStartDate(slotInfo.start);
    setSelectedEndDate(slotInfo.start); // Same day, not next day
    setStartTime('09:00');
    setEndTime('09:30');
    setIsAllDay(false);
    setEventTitle('');
    setEventCategory('');
    setEventDescription('');
    setIsCreateEventModalOpen(true);
  };

  // Handle event creation form submission
  const handleCreateEvent = () => {
    if (!eventTitle.trim()) {
      notify.error('Event title is required');
      return;
    }
    if (!eventCategory) {
      notify.error('Event category is required');
      return;
    }
    if (!selectedStartDate || !selectedEndDate) {
      notify.error('Event dates are required');
      return;
    }

    // Validate times if not all-day
    if (!isAllDay) {
      const [startHour, startMin] = startTime.split(':').map(Number);
      const [endHour, endMin] = endTime.split(':').map(Number);

      const startDateTime = new Date(selectedStartDate);
      startDateTime.setHours(startHour!, startMin!, 0, 0);

      let endDateTime = new Date(selectedEndDate);
      endDateTime.setHours(endHour!, endMin!, 0, 0);

      // If same day and end time is before start time, assume it crosses midnight
      if (
        selectedStartDate.toDateString() === selectedEndDate.toDateString() &&
        endDateTime <= startDateTime
      ) {
        // Add one day to end date
        endDateTime = new Date(endDateTime);
        endDateTime.setDate(endDateTime.getDate() + 1);
      }

      // Final validation: end must still be after start
      if (endDateTime <= startDateTime) {
        notify.error('End time must be after start time');
        return;
      }
    }

    const formData = new FormData();
    formData.append('intent', 'create-event');
    formData.append('title', eventTitle.trim());
    formData.append('category', eventCategory);
    formData.append('isAllDay', String(isAllDay));

    if (isAllDay) {
      formData.append('startDate', format(selectedStartDate, 'yyyy-MM-dd'));
      formData.append('endDate', format(selectedEndDate, 'yyyy-MM-dd'));
    } else {
      // Send datetime in ISO format for timed events
      const [startHour, startMin] = startTime.split(':').map(Number);
      const [endHour, endMin] = endTime.split(':').map(Number);

      const startDateTime = new Date(selectedStartDate);
      startDateTime.setHours(startHour!, startMin!, 0, 0);

      let endDateTime = new Date(selectedEndDate);
      endDateTime.setHours(endHour!, endMin!, 0, 0);

      // If same day and end time is before start time, assume it crosses midnight
      if (
        selectedStartDate.toDateString() === selectedEndDate.toDateString() &&
        endDateTime <= startDateTime
      ) {
        // Add one day to end date
        endDateTime = new Date(endDateTime);
        endDateTime.setDate(endDateTime.getDate() + 1);
      }

      formData.append('startDateTime', startDateTime.toISOString());
      formData.append('endDateTime', endDateTime.toISOString());
    }

    if (eventDescription.trim()) {
      formData.append('description', eventDescription.trim());
    }

    createEventFetcher.submit(formData, {
      method: 'POST',
      action: '/api/calendar-events',
    });
  };

  // Handle event click - only allow editing custom events
  const handleSelectEvent = (event: BahandiCalendarEvent) => {
    if (event.type !== 'custom') {
      // Can't edit holidays, leaves, or birthdays
      return;
    }

    // Extract event ID (format: "custom-{id}")
    const eventId = event.id.replace('custom-', '');
    const eventData = fetcher.data?.events.find(
      (e) => e.id === `custom-${eventId}`,
    );

    if (!eventData || !fetcher.data) return;

    // Check if user can edit this event
    const isCreator = fetcher.data.currentUserId === eventData.createdById;
    const canEdit =
      isCreator || fetcher.data.isAdmin || fetcher.data.isHRManager;
    setCanEditCurrentEvent(canEdit);

    // Populate form with existing data
    setEditingEventId(eventId);
    setEventTitle(eventData.title);
    setEventCategory(eventData.category || '');
    setEventDescription(eventData.description || '');
    setEventCreator(eventData.createdBy || 'Unknown');

    const startDate = new Date(eventData.start);
    const endDate = new Date(eventData.end);

    setSelectedStartDate(startDate);
    setSelectedEndDate(endDate);

    // Check if it's an all-day event or timed event
    const isAllDayEvent = eventData.allDay;
    setIsAllDay(isAllDayEvent);

    // Always set the times from the event data
    // For all-day events, this will be 00:00
    // For timed events, this will be the actual event times
    setStartTime(format(startDate, 'HH:mm'));
    setEndTime(format(endDate, 'HH:mm'));

    setIsEditEventModalOpen(true);
  };

  // Handle event update
  const handleUpdateEvent = () => {
    if (!editingEventId) return;

    if (!eventTitle.trim()) {
      notify.error('Event title is required');
      return;
    }
    if (!eventCategory) {
      notify.error('Event category is required');
      return;
    }
    if (!selectedStartDate || !selectedEndDate) {
      notify.error('Event dates are required');
      return;
    }

    // Validate times if not all-day
    if (!isAllDay) {
      const [startHour, startMin] = startTime.split(':').map(Number);
      const [endHour, endMin] = endTime.split(':').map(Number);

      const startDateTime = new Date(selectedStartDate);
      startDateTime.setHours(startHour!, startMin!, 0, 0);

      let endDateTime = new Date(selectedEndDate);
      endDateTime.setHours(endHour!, endMin!, 0, 0);

      if (
        selectedStartDate.toDateString() === selectedEndDate.toDateString() &&
        endDateTime <= startDateTime
      ) {
        endDateTime = new Date(endDateTime);
        endDateTime.setDate(endDateTime.getDate() + 1);
      }

      if (endDateTime <= startDateTime) {
        notify.error('End time must be after start time');
        return;
      }
    }

    const formData = new FormData();
    formData.append('intent', 'update-event');
    formData.append('eventId', editingEventId);
    formData.append('title', eventTitle.trim());
    formData.append('category', eventCategory);
    formData.append('isAllDay', String(isAllDay));

    if (isAllDay) {
      formData.append('startDate', format(selectedStartDate, 'yyyy-MM-dd'));
      formData.append('endDate', format(selectedEndDate, 'yyyy-MM-dd'));
    } else {
      const [startHour, startMin] = startTime.split(':').map(Number);
      const [endHour, endMin] = endTime.split(':').map(Number);

      const startDateTime = new Date(selectedStartDate);
      startDateTime.setHours(startHour!, startMin!, 0, 0);

      let endDateTime = new Date(selectedEndDate);
      endDateTime.setHours(endHour!, endMin!, 0, 0);

      if (
        selectedStartDate.toDateString() === selectedEndDate.toDateString() &&
        endDateTime <= startDateTime
      ) {
        endDateTime = new Date(endDateTime);
        endDateTime.setDate(endDateTime.getDate() + 1);
      }

      formData.append('startDateTime', startDateTime.toISOString());
      formData.append('endDateTime', endDateTime.toISOString());
    }

    if (eventDescription.trim()) {
      formData.append('description', eventDescription.trim());
    }

    updateEventFetcher.submit(formData, {
      method: 'POST',
      action: '/api/calendar-events',
    });
  };

  // Show delete confirmation modal
  const handleDeleteClick = () => {
    setIsDeleteConfirmModalOpen(true);
  };

  // Confirm and delete event
  const handleConfirmDelete = () => {
    if (!editingEventId) return;

    const formData = new FormData();
    formData.append('intent', 'delete-event');
    formData.append('eventId', editingEventId);

    deleteEventFetcher.submit(formData, {
      method: 'POST',
      action: '/api/calendar-events',
    });

    setIsDeleteConfirmModalOpen(false);
  };

  // Handle create event response
  useEffect(() => {
    if (createEventFetcher.state === 'idle' && createEventFetcher.data) {
      const submissionId = JSON.stringify(createEventFetcher.data);

      if (lastProcessedRef.current === submissionId) {
        return;
      }

      lastProcessedRef.current = submissionId;

      if (createEventFetcher.data.status === 'success') {
        notify.success(createEventFetcher.data.message);
        setIsCreateEventModalOpen(false);
        fetcher.load('/api/calendar-events');
      } else if (createEventFetcher.data.status === 'error') {
        notify.error(createEventFetcher.data.message);
      }
    }
  }, [createEventFetcher.state, createEventFetcher.data, notify, fetcher]);

  // Handle update event response
  useEffect(() => {
    if (updateEventFetcher.state === 'idle' && updateEventFetcher.data) {
      const submissionId = JSON.stringify(updateEventFetcher.data);

      if (lastUpdateProcessedRef.current === submissionId) {
        return;
      }

      lastUpdateProcessedRef.current = submissionId;

      if (updateEventFetcher.data.status === 'success') {
        notify.success(updateEventFetcher.data.message);
        setIsEditEventModalOpen(false);
        fetcher.load('/api/calendar-events');
      } else if (updateEventFetcher.data.status === 'error') {
        notify.error(updateEventFetcher.data.message);
      }
    }
  }, [updateEventFetcher.state, updateEventFetcher.data, notify, fetcher]);

  // Handle delete event response
  useEffect(() => {
    if (deleteEventFetcher.state === 'idle' && deleteEventFetcher.data) {
      const submissionId = JSON.stringify(deleteEventFetcher.data);

      if (lastDeleteProcessedRef.current === submissionId) {
        return;
      }

      lastDeleteProcessedRef.current = submissionId;

      if (deleteEventFetcher.data.status === 'success') {
        notify.success(deleteEventFetcher.data.message);
        setIsEditEventModalOpen(false);
        fetcher.load('/api/calendar-events');
      } else if (deleteEventFetcher.data.status === 'error') {
        notify.error(deleteEventFetcher.data.message);
      }
    }
  }, [deleteEventFetcher.state, deleteEventFetcher.data, notify, fetcher]);

  // Convert server events (with ISO strings) to calendar events (with Date objects)
  const events = useMemo<BahandiCalendarEvent[]>(() => {
    if (!fetcher.data?.events) return [];

    // Color palette for leave requests (same as calendar page)
    const leaveColors = [
      { bg: '#dc2626', text: '#fff' }, // red-600
      { bg: '#16a34a', text: '#fff' }, // green-600
      { bg: '#2563eb', text: '#fff' }, // blue-600
      { bg: '#c026d3', text: '#fff' }, // fuchsia-600
      { bg: '#ea580c', text: '#fff' }, // orange-600
      { bg: '#4f46e5', text: '#fff' }, // indigo-600
      { bg: '#059669', text: '#fff' }, // emerald-600
      { bg: '#e11d48', text: '#fff' }, // rose-600
      { bg: '#0284c7', text: '#fff' }, // sky-600
      { bg: '#ca8a04', text: '#fff' }, // yellow-600
      { bg: '#7c3aed', text: '#fff' }, // violet-600
      { bg: '#0891b2', text: '#fff' }, // cyan-600
      { bg: '#db2777', text: '#fff' }, // pink-600
      { bg: '#65a30d', text: '#fff' }, // lime-600
      { bg: '#d97706', text: '#fff' }, // amber-600
      { bg: '#9333ea', text: '#fff' }, // purple-600
      { bg: '#0d9488', text: '#fff' }, // teal-600
    ];

    let leaveIndex = 0;

    return fetcher.data.events.map((event) => {
      const baseEvent = {
        id: event.id,
        title: event.title,
        start: new Date(event.start),
        end: new Date(event.end),
        type: event.type,
        status: event.status,
        allDay: true as const,
      };

      // Assign color for leave events
      if (event.type === 'leave') {
        const colorPalette = leaveColors[leaveIndex % leaveColors.length]!;
        leaveIndex++;

        return {
          ...baseEvent,
          color: {
            bg: colorPalette.bg,
            border: 'var(--color-primary-base)', // Use primary color for border
            text: colorPalette.text,
          },
        };
      }

      // Assign color for birthday events
      if (event.type === 'birthday') {
        return {
          ...baseEvent,
          color: {
            bg: '#ec4899', // pink-500
            border: '#ec4899',
            text: '#fff',
          },
        };
      }

      // Assign color for custom events based on category
      if (event.type === 'custom' && event.category) {
        const categoryColor = getCategoryColor(event.category);
        return {
          ...baseEvent,
          category: event.category,
          color: categoryColor,
        };
      }

      return baseEvent;
    });
  }, [fetcher.data]);

  // Filter events based on toggle states and sort by priority
  const filteredEvents = events
    .filter((event) => {
      if (event.type === 'custom' && !showCustomEvents) return false;
      if (event.type === 'leave' && !showLeaves) return false;
      if (event.type === 'holiday' && !showHolidays) return false;
      if (event.type === 'birthday' && !showBirthdays) return false;
      return true;
    })
    .sort((a, b) => {
      // Priority: holiday > leave > custom > birthday
      const priority = { holiday: 0, leave: 1, custom: 2, birthday: 3 };
      return priority[a.type] - priority[b.type];
    });

  return (
    <>
      <HeaderGlobalAction
        aria-label="Calendar"
        tooltipAlignment="end"
        onClick={handleOpen}
      >
        <CalendarIcon size={20} className="text-white/70! hover:text-white!" />
      </HeaderGlobalAction>

      <Modal
        open={isOpen}
        onRequestClose={() => setIsOpen(false)}
        passiveModal
        modalHeading="Calendar"
        size="lg"
        className="[&_.cds--modal-content]:px-0! [&_.cds--modal-content]:pb-0! [&_.cds--modal-content]:mask-none! [&_.cds--modal-content]:max-h-[calc(100vh-8rem)]! [&_.cds--modal-content]:overflow-y-auto! [&_.cds--modal-container]:max-h-[calc(100vh-4rem)]!"
        selectorsFloatingMenus={[
          '.cds--overflow-menu-options',
          '.cds--tooltip',
          '.flatpickr-calendar',
          '.cds--list-box__menu', // Carbon Select dropdown menu from Modal B
          '.cds--list-box', // Carbon Select container from Modal B
          '.cds--dropdown', // Carbon Dropdown from Modal B
          '.cds--multiselect', // Carbon Multiselect from Modal B
          '.cds--modal-container', // Allow the second modal to be interacted with
        ]}
      >
        <div className="pb-4x">
          {/* Loading State */}
          {fetcher.state === 'loading' && events.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <CalendarIcon className="mx-auto mb-2 w-8 h-8" />
              <p>Loading calendar...</p>
            </div>
          )}

          {/* Calendar */}
          {(fetcher.state === 'idle' || events.length > 0) && (
            <div>
              <BahandiCalendar
                events={filteredEvents}
                currentDate={currentDate}
                onNavigate={setCurrentDate}
                userTimezone={userTimezone}
                showCustomEvents={showCustomEvents}
                showLeaves={showLeaves}
                showHolidays={showHolidays}
                showBirthdays={showBirthdays}
                onToggleCustomEvents={() =>
                  setShowCustomEvents(!showCustomEvents)
                }
                onToggleLeaves={() => setShowLeaves(!showLeaves)}
                onToggleHolidays={() => setShowHolidays(!showHolidays)}
                onToggleBirthdays={() => setShowBirthdays(!showBirthdays)}
                selectable={true}
                onSelectSlot={handleSelectSlot}
                onSelectEvent={handleSelectEvent}
              />
            </div>
          )}
        </div>
      </Modal>

      {/* Create Event Modal */}
      <Modal
        open={isCreateEventModalOpen}
        onRequestClose={() => setIsCreateEventModalOpen(false)}
        modalHeading="Create Calendar Event"
        primaryButtonText="Create Event"
        secondaryButtonText="Cancel"
        onRequestSubmit={handleCreateEvent}
        size="sm"
        selectorsFloatingMenus={[
          '.cds--overflow-menu-options',
          '.cds--tooltip',
          '.flatpickr-calendar',
          '.cds--list-box__menu', // Carbon Select dropdown menu
          '.cds--list-box', // Carbon Select container
          '.cds--dropdown', // Carbon Dropdown
          '.cds--multiselect', // Carbon Multiselect
        ]}
      >
        <div className="space-y-4">
          <TextInput
            id="event-title"
            labelText="Event Title"
            placeholder="e.g., Team Standup"
            value={eventTitle}
            onChange={(e) => setEventTitle(e.target.value)}
            required
          />

          <Select
            id="event-category"
            labelText="Event Category"
            value={eventCategory}
            onChange={(e) => setEventCategory(e.target.value)}
            required
          >
            <SelectItem value="" text="Select category" />
            <SelectItem value="CTRLSPIRE_MEETING" text="CtrlSpire Meeting" />
            <SelectItem value="SWELLFOX_MEETING" text="SwellFox Meeting" />
            <SelectItem value="CLIENT_MEETING" text="Client Meeting" />
            <SelectItem value="FUN_HOUR" text="Fun Hour" />
            <SelectItem value="TEAM_BUILDING" text="Team Building" />
            <SelectItem value="ANNOUNCEMENT" text="Announcement" />
          </Select>

          <Checkbox
            id="is-all-day"
            labelText="All-day event"
            checked={isAllDay}
            onChange={(e) => setIsAllDay(e.target.checked)}
          />

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="cds--label text-xs mb-1 block">
                Start Date
              </label>
              <input
                type="date"
                className="cds--text-input w-full scheme-light dark:scheme-dark"
                value={
                  selectedStartDate
                    ? format(selectedStartDate, 'yyyy-MM-dd')
                    : ''
                }
                onChange={(e) =>
                  setSelectedStartDate(
                    e.target.value ? new Date(e.target.value) : null,
                  )
                }
                required
              />
            </div>
            <div>
              <label className="cds--label text-xs mb-1 block">End Date</label>
              <input
                type="date"
                className="cds--text-input w-full scheme-light dark:scheme-dark"
                value={
                  selectedEndDate ? format(selectedEndDate, 'yyyy-MM-dd') : ''
                }
                onChange={(e) =>
                  setSelectedEndDate(
                    e.target.value ? new Date(e.target.value) : null,
                  )
                }
                required
              />
            </div>
          </div>

          {!isAllDay && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="cds--label text-xs mb-1 block">
                  Start Time
                </label>
                <input
                  type="time"
                  className="cds--text-input w-full"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="cds--label text-xs mb-1 block">
                  End Time
                </label>
                <input
                  type="time"
                  className="cds--text-input w-full"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  required
                />
              </div>
            </div>
          )}

          <TextArea
            id="event-description"
            labelText="Description (Optional)"
            placeholder="Add any additional details..."
            value={eventDescription}
            onChange={(e) => setEventDescription(e.target.value)}
            rows={3}
          />
        </div>
      </Modal>

      {/* Edit Event Modal */}
      <Modal
        open={isEditEventModalOpen}
        onRequestClose={() => setIsEditEventModalOpen(false)}
        modalHeading={
          canEditCurrentEvent ? 'Edit Calendar Event' : 'View Calendar Event'
        }
        primaryButtonText={canEditCurrentEvent ? 'Update Event' : undefined}
        secondaryButtonText="Close"
        onRequestSubmit={canEditCurrentEvent ? handleUpdateEvent : undefined}
        size="sm"
        selectorPrimaryFocus=".cds--modal-container"
        selectorsFloatingMenus={[
          '.cds--overflow-menu-options',
          '.cds--tooltip',
          '.flatpickr-calendar',
          '.cds--list-box__menu',
          '.cds--list-box',
          '.cds--dropdown',
          '.cds--multiselect',
        ]}
      >
        {/* Custom styles for view-only mode */}
        {!canEditCurrentEvent && (
          <style>{`
            .view-only-input input.cds--text-input:disabled,
            .view-only-input textarea.cds--text-area:disabled,
            input.cds--text-input:disabled,
            textarea.cds--text-area:disabled,
            select.cds--select-input:disabled {
              background-color: #ffffff !important;
              color: #161616 !important;
              border-color: #8d8d8d !important;
              opacity: 1 !important;
              cursor: not-allowed !important;
              -webkit-text-fill-color: #161616 !important;
            }
            .view-only-input label.cds--label--disabled,
            label.cds--label--disabled {
              color: #525252 !important;
              opacity: 1 !important;
            }
            input.cds--checkbox:disabled + label.cds--checkbox-label .cds--checkbox-label-text {
              color: #161616 !important;
              opacity: 1 !important;
            }
          `}</style>
        )}
        <div className="space-y-4">
          {/* Show unauthorized message if user can't edit */}
          {!canEditCurrentEvent && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 text-sm text-yellow-800">
              You can only view this event. Only the creator, admins, and HR
              managers can edit or delete events.
            </div>
          )}

          {/* Creator info and Delete Button */}
          <div className="flex justify-between items-center -mt-2 mb-4 pb-3 border-b border-gray-600">
            <div className="text-sm text-gray-500">
              {eventCreator && (
                <>
                  Created by:{' '}
                  <span className="text-bahandi-02 dark:text-white">
                    {eventCreator}
                  </span>
                </>
              )}
            </div>
            {canEditCurrentEvent && (
              <Button
                kind="danger--ghost"
                size="sm"
                onClick={handleDeleteClick}
              >
                Delete Event
              </Button>
            )}
          </div>

          <div className={!canEditCurrentEvent ? 'view-only-input' : ''}>
            <TextInput
              id="edit-event-title"
              labelText="Event Title"
              placeholder="e.g., Team Standup"
              value={eventTitle}
              onChange={(e) => setEventTitle(e.target.value)}
              disabled={!canEditCurrentEvent}
              required
            />
          </div>

          <Select
            id="edit-event-category"
            labelText="Event Category"
            value={eventCategory}
            onChange={(e) => setEventCategory(e.target.value)}
            disabled={!canEditCurrentEvent}
            required
          >
            <SelectItem value="" text="Select category" />
            <SelectItem value="CTRLSPIRE_MEETING" text="CtrlSpire Meeting" />
            <SelectItem value="SWELLFOX_MEETING" text="SwellFox Meeting" />
            <SelectItem value="CLIENT_MEETING" text="Client Meeting" />
            <SelectItem value="FUN_HOUR" text="Fun Hour" />
            <SelectItem value="TEAM_BUILDING" text="Team Building" />
            <SelectItem value="ANNOUNCEMENT" text="Announcement" />
          </Select>

          <Checkbox
            id="edit-is-all-day"
            labelText="All-day event"
            checked={isAllDay}
            onChange={(e) => setIsAllDay(e.target.checked)}
            disabled={!canEditCurrentEvent}
          />

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="cds--label text-xs mb-1 block">
                Start Date
              </label>
              <input
                type="date"
                className="cds--text-input w-full scheme-light dark:scheme-dark"
                value={
                  selectedStartDate
                    ? format(selectedStartDate, 'yyyy-MM-dd')
                    : ''
                }
                onChange={(e) =>
                  setSelectedStartDate(
                    e.target.value ? new Date(e.target.value) : null,
                  )
                }
                disabled={!canEditCurrentEvent}
                required
              />
            </div>
            <div>
              <label className="cds--label text-xs mb-1 block">End Date</label>
              <input
                type="date"
                className="cds--text-input w-full scheme-light dark:scheme-dark"
                value={
                  selectedEndDate ? format(selectedEndDate, 'yyyy-MM-dd') : ''
                }
                onChange={(e) =>
                  setSelectedEndDate(
                    e.target.value ? new Date(e.target.value) : null,
                  )
                }
                disabled={!canEditCurrentEvent}
                required
              />
            </div>
          </div>

          {!isAllDay && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="cds--label text-xs mb-1 block">
                  Start Time
                </label>
                <input
                  type="time"
                  className="cds--text-input w-full"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  disabled={!canEditCurrentEvent}
                  required
                />
              </div>
              <div>
                <label className="cds--label text-xs mb-1 block">
                  End Time
                </label>
                <input
                  type="time"
                  className="cds--text-input w-full"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  disabled={!canEditCurrentEvent}
                  required
                />
              </div>
            </div>
          )}

          <TextArea
            id="edit-event-description"
            labelText="Description (Optional)"
            placeholder="Add any additional details..."
            value={eventDescription}
            onChange={(e) => setEventDescription(e.target.value)}
            disabled={!canEditCurrentEvent}
            rows={3}
          />
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        open={isDeleteConfirmModalOpen}
        onRequestClose={() => setIsDeleteConfirmModalOpen(false)}
        modalHeading="Delete Calendar Event"
        primaryButtonText="Delete"
        secondaryButtonText="Cancel"
        onRequestSubmit={handleConfirmDelete}
        danger
        size="xs"
      >
        <p className="mb-4">
          Are you sure you want to delete "<strong>{eventTitle}</strong>"?
        </p>
        <p className="text-sm text-gray-600">This action cannot be undone.</p>
      </Modal>
    </>
  );
}
